<template>
  <div class="modal fade" :id="id" tabindex="-1">
    <div class="modal-dialog modal-lg" >
      <form @submit.prevent="submitForm" class="form">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEdit ? $t('crm.pipeline.activity.update') : $t('crm.pipeline.activity.add') }}</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" @click="closeModal">
                <span class="svg-icon svg-icon-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)"
                          fill="currentColor" />
                    <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                          fill="currentColor" />
                  </svg>
                </span>
            </div>
          </div>
          <div class="modal-body">
            <EmailForm v-if="activityFormData.type_activity === activityTypeEnum.MAIL" :form="activityFormData" :id="id"/>
            <CallForm v-else-if="activityFormData.type_activity === activityTypeEnum.CALL" :form="activityFormData" :id="id"/>
            <RdvForm v-else-if="activityFormData.type_activity === activityTypeEnum.RDV" :form="activityFormData" :id="id"/>
            <NoteForm v-else-if="activityFormData.type_activity === activityTypeEnum.NOTE" :form="activityFormData" :id="id"/>
            <CustomForm v-else :form="activityFormData" :id="id"/>
            <CKEditor
                :id="`${id}_ck_editor`"
                :required="false"
                :label="$t('crm.label.resume')"
                :content="activityFormData.note"
                @text="getDescription"/>
            <br>
            <DropFileZone
                :id="`${id}_upload_file`"
                :files="activityFormData.files"
                @addFiles="addFiles"
                @removeFiles="removeFiles"
            />
          </div>
          <div class="modal-footer">
            <Button type="cancel" id="cancel_btn" btn-class="btn-sm me-3" size="7px 30px" btn-text="button.cancel" @click="closeModal"></Button>
            <Button btn-class="btn-sm me-3" type="submit" id="submit_btn" btn-text="button.validate" size="7px 30px" :is-loading="isLoading"></Button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import {ref, defineEmits, defineProps, computed, watch} from "vue";
import {useStore} from "vuex";
import Button from "@/components/Button.vue"
import EmailForm from "@/Modules/CRM/components/Modals/Activity/email/EmailForm";
import activityTypeEnum from "@/Modules/CRM/composable/activityTypes";
import {createFormData, compareDateToToday} from "@/mixins/Helpers";
import CallForm from "@/Modules/CRM/components/Modals/Activity/call/CallForm";
import RdvForm from "@/Modules/CRM/components/Modals/Activity/rdv/RdvForm";
import NoteForm from "@/Modules/CRM/components/Modals/Activity/note/NoteForm";
import CustomForm from "@/Modules/CRM/components/Modals/Activity/custom/CustomForm";
import CKEditor from "@/components/CKEditor.vue";
import DropFileZone from "@/components/DropFileZone.vue";

const store = useStore()

const emits = defineEmits(['activityCreated', 'activityUpdated'])
const props = defineProps({
  id: {
    type: String,
    required: true
  },
  isEdit: { type: Boolean, default: false},
  formData: {
    type: Object,
    default: () => ({
      type_activity: 'EMAIL',
      opportunity_id: '',
      activity_id: '',
      type_activity_id: '',
      files: [],
      type: null,
      title: null,
      priority: null,
      dateTime: null,
      note: '',
      date: '',
      time: '',
      detach_files: [],
      remind_frequency_count: null,
      remind_frequency: null
    })
  },
})

const activityFormData = ref({
  type_activity: 'EMAIL',
  opportunity_id: '',
  activity_id: '',
  type_activity_id: '',
  files: [],
  type: null,
  title: null,
  priority: null,
  dateTime: null,
  note: '',
  date: '',
  time: '',
  detach_files: [],
  remind_frequency_count: null,
  remind_frequency: null
})
const isLoading = ref(false)
const activityNote = ref('')

watch(() => props.formData, (value) => {
  activityFormData.value = {...value}
  activityNote.value = value.note || ''
})

const past = computed(() => {
  let datetime = activityFormData.value.date + ' ' + activityFormData.value.time
  return compareDateToToday(activityFormData.value.date+' 00:00') || compareDateToToday(datetime)
})

function closeModal(){
  activityFormData.value = {
    type_activity: 'EMAIL',
    opportunity_id: '',
    type_activity_id: '',
    files: [],
    type: null,
    title: null,
    priority: null,
    dateTime: null,
    note: '',
    date: '',
    time: '',
    detach_files: [],
    remind_frequency_count: null,
    remind_frequency: null
  }
  $('#'+props.id).modal('toggle')
}

function getDescription(text){
  activityNote.value = text
}

function addFiles(files){
  for(let file of files) {
    activityFormData.value.files.push(file)
  }
}

function removeFiles(index) {
  const fileToDelete = activityFormData.value.files.splice(index, 1)
  activityFormData.value.detach_files.push(fileToDelete[0]?.id)
}

function submitForm() {
  activityFormData.value.note = activityNote.value
  if(!props.isEdit) {
    createActivity()
  }else {
    updateActivity()
  }
}

function createActivity() {
  isLoading.value = true
  if(!past.value){
    activityFormData.value.remind_frequency_count = null
    activityFormData.value.remind_frequency = null
  }
  activityFormData.value.type_activity_id = props.formData.type_activity_id
  activityFormData.value.dateTime = activityFormData.value.date + ' ' + activityFormData.value.time
  const formData = createFormData(activityFormData.value)

  store.dispatch('activity/createActivity', formData)
      .then(res => {
        closeModal()
        window.toastr['success'](i18n.global.t("notification.activity_created"))
        activityFormData.value = {
          type_activity: 'EMAIL',
          opportunity_id: '',
          type_activity_id: '',
          files: [],
          type: null,
          title: null,
          priority: null,
          dateTime: null,
          note: '',
          date: '',
          time: '',
          detach_files: [],
          remind_frequency_count: null,
          remind_frequency: null
        }
        emits('activityCreated')
      })
      .catch((err) => {

      })
      .finally(() => isLoading.value = false)
}

function updateActivity() {
  isLoading.value = true
  if(!past.value){
    activityFormData.value.remind_frequency_count = null
    activityFormData.value.remind_frequency = null
  }
  activityFormData.value.type_activity_id = props.formData.type_activity_id
  activityFormData.value.dateTime = activityFormData.value.date + ' ' + activityFormData.value.time
  activityFormData.value.files= activityFormData.value.files.filter(file => file instanceof File)

  store.dispatch('activity/updateActivity', {id: props.formData.activity_id, data: activityFormData.value})
      .then(res => {
        closeModal()
        window.toastr['success'](i18n.global.t("notification.activity_updated"))
        activityFormData.value = {
          type_activity: 'EMAIL',
          opportunity_id: '',
          type_activity_id: '',
          files: [],
          type: null,
          title: null,
          priority: null,
          dateTime: null,
          note: '',
          date: '',
          time: '',
          detach_files: [],
          remind_frequency_count: null,
          remind_frequency: null
        }
        emits('activityUpdated')
      })
      .catch((err) => {

      })
      .finally(() => isLoading.value = false)

}

</script>

<style scoped></style>
